// Hooks TanStack Query de Autorização (Parte 5.7, 12.1; GIY-056) sobre o cliente tipado gerado do contrato. Chaves
// começam por `['authz', 'network', …]`, `['authz', 'tenant', tenantId, …]` ou `['authz', 'me', …]` para o clear de cache
// na troca de contexto (regra 6 de 14.7). Mutações são `silent`: cada tela mostra o erro inline (ProblemAlert); as que
// exigem step-up (A.1: U) são envolvidas por `useStepUpAction` na tela. Leituras sensíveis (auditoria, point-in-time —
// A.1: P) só rodam com um propósito declarado (`X-Purpose`), pedido pelo `PurposeDialog`.
import { type DeclaredPurpose, type components, formatPurposeHeader } from '@giyus/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../../../lib/api/apiClientContext';

type Schemas = components['schemas'];

export type Role = Schemas['AuthorizationRoleResponse'];
export type Permission = Schemas['AuthorizationPermissionResponse'];
export type MatrixVersionSummary = Schemas['AuthorizationMatrixVersionSummaryResponse'];
export type MatrixVersion = Schemas['AuthorizationMatrixVersionResponse'];
export type MatrixRoleEntries = Schemas['AuthorizationMatrixRoleEntriesResponse'];
export type UpdateMatrixVersionInput = Schemas['AuthorizationUpdateMatrixVersionRequest'];
export type RoleAssignment = Schemas['AuthorizationRoleAssignmentResponse'];
export type GrantAssignmentInput = Schemas['AuthorizationGrantAssignmentRequest'];
export type SodRule = Schemas['AuthorizationSodRuleResponse'];
export type CreateSodRuleInput = Schemas['AuthorizationCreateSodRuleRequest'];
export type UpdateSodRuleInput = Schemas['AuthorizationUpdateSodRuleRequest'];
export type BreakGlassEvent = Schemas['AuthorizationBreakGlassEventResponse'];
export type OpenBreakGlassInput = Schemas['AuthorizationOpenBreakGlassRequest'];
export type AccessReviewCampaign = Schemas['AuthorizationAccessReviewCampaignResponse'];
export type AccessReviewItem = Schemas['AuthorizationAccessReviewItemResponse'];
export type TwoKeyApproval = Schemas['AuthorizationTwoKeyApprovalResponse'];
export type PointInTimeReport = Schemas['AuthorizationPointInTimeResponse'];
export type PointInTimeAssignment = Schemas['PointInTimeAssignment'];
export type AuditEvent = Schemas['AuditEventDto'];
export type AuditPage = Schemas['CursorPageOfAuditEventDto'];
export type ScopeType = Schemas['ScopeType'];
export type AssignmentStatusFilter = Schemas['AssignmentStatusFilter'];
export type SodRuleKind = Schemas['SodRuleKind'];
export type SodRuleStatus = Schemas['SodRuleStatus'];
export type BreakGlassStatusFilter = Schemas['BreakGlassStatusFilter'];
export type AccessReviewItemFilter = Schemas['AccessReviewItemFilter'];
export type TwoKeyApprovalStatus = Schemas['TwoKeyApprovalStatus'];

export const SCOPE_TYPES: readonly ScopeType[] = ['Network', 'Tenant', 'Site', 'Study'];
export const TENANT_SCOPE_TYPES: readonly ScopeType[] = ['Tenant', 'Site', 'Study'];
export const ASSIGNMENT_STATUS_FILTERS: readonly AssignmentStatusFilter[] = [
  'Active',
  'Scheduled',
  'Expired',
  'Revoked',
  'All',
];
export const SOD_RULE_KINDS: readonly SodRuleKind[] = ['Act', 'Assignment'];
export const SOD_RULE_STATUSES: readonly SodRuleStatus[] = [
  'PendingSecondKey',
  'Active',
  'Ended',
  'Cancelled',
];
export const BREAK_GLASS_STATUS_FILTERS: readonly BreakGlassStatusFilter[] = [
  'PendingReview',
  'Active',
  'Ended',
  'Reviewed',
  'All',
];
export const ACCESS_REVIEW_ITEM_FILTERS: readonly AccessReviewItemFilter[] = [
  'All',
  'Pending',
  'Decided',
  'Escalated',
];
export const TWO_KEY_STATUSES: readonly TwoKeyApprovalStatus[] = [
  'PendingSecondKey',
  'Completed',
  'Expired',
  'Cancelled',
];
export const AUDIT_EXPORT_FORMATS = ['jsonl', 'csv'] as const;
export type AuditExportFormat = (typeof AUDIT_EXPORT_FORMATS)[number];

export const networkKey = ['authz', 'network'] as const;
export const tenantKey = (tenantId: string) => ['authz', 'tenant', tenantId] as const;
export const meKey = ['authz', 'me'] as const;

/** Recurso com versão: o `ETag` do cabeçalho é o contrato do `If-Match` (Parte 13). */
export interface Versioned<T> {
  readonly data: T;
  readonly etag: string | null;
}

function etagOf(response: Response): string | null {
  return response.headers.get('ETag');
}

/** Comandos idempotentes (Parte 13.1): o contrato exige a chave; uma nova por tentativa. */
function idempotency(): { 'Idempotency-Key': string } {
  return { 'Idempotency-Key': crypto.randomUUID() };
}

/** `If-Match` do `PUT` (412 sem ele). */
function ifMatch(etag: string | null): { 'If-Match': string } {
  return { 'If-Match': etag ?? '*' };
}

function purposeHeader(purpose: DeclaredPurpose): { 'X-Purpose': string } {
  return { 'X-Purpose': formatPurposeHeader(purpose) };
}

function required<T>(data: T | undefined): T {
  if (data === undefined) {
    throw new Error('Empty response');
  }
  return data;
}

function useInvalidate(keys: readonly (readonly string[])[]) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  };
}

// ---- Catálogo (REQ-AUTHZ-001/002) ----

/** Catálogo de papéis (`authz.read`, rota de rede): quem não tem acesso de rede não o consulta (`enabled: false`). */
export function useRoles(options: { readonly enabled?: boolean } = {}) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'roles'],
    queryFn: async (): Promise<Role[]> => {
      const { data } = await client.GET('/api/v1/network/authz/roles');
      return required(data);
    },
    enabled: options.enabled ?? true,
    meta: { silent: true },
  });
}

export function usePermissionCatalog() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'permissions'],
    queryFn: async (): Promise<Permission[]> => {
      const { data } = await client.GET('/api/v1/network/authz/permissions');
      return required(data);
    },
    meta: { silent: true },
  });
}

// ---- Matriz papel × permissão (REQ-AUTHZ-003) ----

export function useMatrixVersions() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'matrix'],
    queryFn: async (): Promise<MatrixVersionSummary[]> => {
      const { data } = await client.GET('/api/v1/network/authz/matrix/versions');
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useMatrixVersion(versionId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'matrix', versionId],
    queryFn: async (): Promise<Versioned<MatrixVersion>> => {
      const { data, response } = await client.GET(
        '/api/v1/network/authz/matrix/versions/{versionId}',
        { params: { path: { versionId } } },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(versionId),
    meta: { silent: true },
  });
}

export function useCreateMatrixVersion() {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'matrix']]);
  return useMutation({
    mutationFn: async (changeSummary: string | null): Promise<MatrixVersion> => {
      const { data } = await client.POST('/api/v1/network/authz/matrix/versions', {
        params: { header: idempotency() },
        body: { changeSummary },
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useUpdateMatrixVersion(versionId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'matrix']]);
  return useMutation({
    mutationFn: async (args: {
      readonly etag: string | null;
      readonly input: UpdateMatrixVersionInput;
    }): Promise<Versioned<MatrixVersion>> => {
      const { data, response } = await client.PUT(
        '/api/v1/network/authz/matrix/versions/{versionId}',
        { params: { path: { versionId }, header: ifMatch(args.etag) }, body: args.input },
      );
      return { data: required(data), etag: etagOf(response) };
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function usePublishMatrixVersion(versionId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'matrix'], meKey]);
  return useMutation({
    mutationFn: async (): Promise<MatrixVersion> => {
      const { data } = await client.POST(
        '/api/v1/network/authz/matrix/versions/{versionId}/publish',
        { params: { path: { versionId }, header: idempotency() } },
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useMatrixSecondKey(versionId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'matrix'], meKey]);
  return useMutation({
    mutationFn: async (comment: string | null): Promise<MatrixVersion> => {
      const { data } = await client.POST(
        '/api/v1/network/authz/matrix/versions/{versionId}/publish/second-key',
        { params: { path: { versionId }, header: idempotency() }, body: { comment } },
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Atribuições (REQ-AUTHZ-004/025) ----

export interface NetworkAssignmentFilters {
  readonly userId?: string | undefined;
  readonly scopeType?: ScopeType | undefined;
  readonly scopeId?: string | undefined;
  readonly status?: AssignmentStatusFilter | undefined;
}

export function useNetworkAssignments(filters: NetworkAssignmentFilters) {
  const client = useApiClient();
  const query = {
    ...(filters.userId ? { userId: filters.userId } : {}),
    ...(filters.scopeType ? { scopeType: filters.scopeType } : {}),
    ...(filters.scopeId ? { scopeId: filters.scopeId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
  };
  return useQuery({
    queryKey: [...networkKey, 'assignments', filters],
    queryFn: async (): Promise<RoleAssignment[]> => {
      const { data } = await client.GET('/api/v1/network/authz/assignments', {
        params: { query },
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

export interface TenantAssignmentFilters {
  readonly userId?: string | undefined;
  readonly status?: AssignmentStatusFilter | undefined;
}

export function useTenantAssignments(tenantId: string, filters: TenantAssignmentFilters) {
  const client = useApiClient();
  const query = {
    ...(filters.userId ? { userId: filters.userId } : {}),
    ...(filters.status ? { status: filters.status } : {}),
  };
  return useQuery({
    queryKey: [...tenantKey(tenantId), 'assignments', filters],
    queryFn: async (): Promise<RoleAssignment[]> => {
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/authz/assignments', {
        params: { path: { tenantId }, query },
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

export type AssignmentScope =
  { readonly kind: 'network' } | { readonly kind: 'tenant'; readonly tenantId: string };

function assignmentKeys(scope: AssignmentScope): readonly (readonly string[])[] {
  return scope.kind === 'network'
    ? [[...networkKey, 'assignments']]
    : [
        [...tenantKey(scope.tenantId), 'assignments'],
        [...networkKey, 'assignments'],
      ];
}

export function useGrantAssignment(scope: AssignmentScope) {
  const client = useApiClient();
  const invalidate = useInvalidate(assignmentKeys(scope));
  return useMutation({
    mutationFn: async (input: GrantAssignmentInput): Promise<RoleAssignment> => {
      if (scope.kind === 'network') {
        const { data } = await client.POST('/api/v1/network/authz/assignments', {
          params: { header: idempotency() },
          body: input,
        });
        return required(data);
      }
      const { data } = await client.POST('/api/v1/tenants/{tenantId}/authz/assignments', {
        params: { path: { tenantId: scope.tenantId }, header: idempotency() },
        body: input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useRevokeAssignment(scope: AssignmentScope) {
  const client = useApiClient();
  const invalidate = useInvalidate(assignmentKeys(scope));
  return useMutation({
    mutationFn: async (args: {
      readonly assignmentId: string;
      readonly reason: string;
    }): Promise<RoleAssignment> => {
      if (scope.kind === 'network') {
        const { data } = await client.POST(
          '/api/v1/network/authz/assignments/{assignmentId}/revoke',
          {
            params: { path: { assignmentId: args.assignmentId }, header: idempotency() },
            body: { reason: args.reason },
          },
        );
        return required(data);
      }
      const { data } = await client.POST(
        '/api/v1/tenants/{tenantId}/authz/assignments/{assignmentId}/revoke',
        {
          params: {
            path: { tenantId: scope.tenantId, assignmentId: args.assignmentId },
            header: idempotency(),
          },
          body: { reason: args.reason },
        },
      );
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Regras de SoD (REQ-AUTHZ-014) ----

export interface SodRuleFilters {
  readonly kind?: SodRuleKind | undefined;
  readonly status?: SodRuleStatus | undefined;
}

export function useSodRules(filters: SodRuleFilters) {
  const client = useApiClient();
  const query = {
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.status ? { status: filters.status } : {}),
  };
  return useQuery({
    queryKey: [...networkKey, 'sod-rules', filters],
    queryFn: async (): Promise<SodRule[]> => {
      const { data } = await client.GET('/api/v1/network/authz/sod-rules', { params: { query } });
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useSodRule(ruleId: string) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'sod-rules', ruleId],
    queryFn: async (): Promise<Versioned<SodRule>> => {
      const { data, response } = await client.GET('/api/v1/network/authz/sod-rules/{ruleId}', {
        params: { path: { ruleId } },
      });
      return { data: required(data), etag: etagOf(response) };
    },
    enabled: Boolean(ruleId),
    meta: { silent: true },
  });
}

export function useCreateSodRule() {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'sod-rules'], meKey]);
  return useMutation({
    mutationFn: async (input: CreateSodRuleInput): Promise<SodRule> => {
      const { data } = await client.POST('/api/v1/network/authz/sod-rules', {
        params: { header: idempotency() },
        body: input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useUpdateSodRule() {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'sod-rules'], meKey]);
  return useMutation({
    mutationFn: async (args: {
      readonly ruleId: string;
      readonly etag: string | null;
      readonly input: UpdateSodRuleInput;
    }): Promise<SodRule> => {
      const { data } = await client.PUT('/api/v1/network/authz/sod-rules/{ruleId}', {
        params: { path: { ruleId: args.ruleId }, header: ifMatch(args.etag) },
        body: args.input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Break-glass (REQ-AUTHZ-016) ----

export interface BreakGlassFilters {
  readonly status?: BreakGlassStatusFilter | undefined;
  readonly tenantId?: string | undefined;
}

export function useBreakGlassQueue(filters: BreakGlassFilters) {
  const client = useApiClient();
  const query = {
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.tenantId ? { tenantId: filters.tenantId } : {}),
  };
  return useQuery({
    queryKey: [...networkKey, 'break-glass', filters],
    queryFn: async (): Promise<BreakGlassEvent[]> => {
      const { data } = await client.GET('/api/v1/network/authz/break-glass', {
        params: { query },
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useOpenBreakGlass(tenantId: string) {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'break-glass']]);
  return useMutation({
    mutationFn: async (input: OpenBreakGlassInput): Promise<BreakGlassEvent> => {
      const { data } = await client.POST('/api/v1/tenants/{tenantId}/authz/break-glass', {
        params: { path: { tenantId }, header: idempotency() },
        body: input,
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

export function useReviewBreakGlass() {
  const client = useApiClient();
  const invalidate = useInvalidate([[...networkKey, 'break-glass']]);
  return useMutation({
    mutationFn: async (args: {
      readonly eventId: string;
      readonly outcome: 'Justified' | 'Unjustified';
      readonly note: string | null;
    }): Promise<BreakGlassEvent> => {
      const { data } = await client.POST('/api/v1/network/authz/break-glass/{eventId}/review', {
        params: { path: { eventId: args.eventId }, header: idempotency() },
        body: { outcome: args.outcome, note: args.note },
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Recertificação (REQ-AUTHZ-017) ----

export function useAccessReviewCampaigns() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'access-reviews'],
    queryFn: async (): Promise<AccessReviewCampaign[]> => {
      const { data } = await client.GET('/api/v1/network/authz/access-reviews');
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useAccessReviewItems(campaignId: string, status: AccessReviewItemFilter) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'access-reviews', campaignId, status],
    queryFn: async (): Promise<AccessReviewItem[]> => {
      const { data } = await client.GET('/api/v1/network/authz/access-reviews/{campaignId}/items', {
        params: { path: { campaignId }, query: { status } },
      });
      return required(data);
    },
    enabled: Boolean(campaignId),
    meta: { silent: true },
  });
}

export function useMyAccessReviewItems() {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'access-reviews'],
    queryFn: async (): Promise<AccessReviewItem[]> => {
      const { data } = await client.GET('/api/v1/authz/access-reviews/my-items');
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useDecideAccessReview() {
  const client = useApiClient();
  const invalidate = useInvalidate([
    [...meKey, 'access-reviews'],
    [...networkKey, 'access-reviews'],
  ]);
  return useMutation({
    mutationFn: async (args: {
      readonly itemId: string;
      readonly decision: 'Keep' | 'Revoke';
      readonly note: string | null;
    }): Promise<AccessReviewItem> => {
      const { data } = await client.POST('/api/v1/authz/access-reviews/items/{itemId}/decide', {
        params: { path: { itemId: args.itemId }, header: idempotency() },
        body: { decision: args.decision, note: args.note },
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Duas chaves (REQ-AUTHZ-015) ----

export function useTwoKeyApprovals(mine: boolean, status: TwoKeyApprovalStatus) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...meKey, 'two-key', mine, status],
    queryFn: async (): Promise<TwoKeyApproval[]> => {
      const { data } = await client.GET('/api/v1/authz/two-key', {
        params: { query: { mine, status } },
      });
      return required(data);
    },
    meta: { silent: true },
  });
}

export function useTwoKeySecondKey() {
  const client = useApiClient();
  const invalidate = useInvalidate([meKey, networkKey]);
  return useMutation({
    mutationFn: async (args: {
      readonly approvalId: string;
      readonly comment: string | null;
    }): Promise<TwoKeyApproval> => {
      const { data } = await client.POST('/api/v1/authz/two-key/{approvalId}/second-key', {
        params: { path: { approvalId: args.approvalId }, header: idempotency() },
        body: { comment: args.comment },
      });
      return required(data);
    },
    onSuccess: invalidate,
    meta: { silent: true },
  });
}

// ---- Point-in-time (REQ-AUTHZ-021) ----

export interface PointInTimeParams {
  readonly date: string;
  readonly userId?: string | undefined;
  readonly cursor?: string | undefined;
}

export function usePointInTime(params: PointInTimeParams | null, purpose: DeclaredPurpose | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...networkKey, 'point-in-time', params, purpose?.code ?? null],
    queryFn: async (): Promise<PointInTimeReport> => {
      if (!params || !purpose) {
        throw new Error('point-in-time needs a date and a declared purpose');
      }
      const { data } = await client.GET('/api/v1/network/authz/point-in-time', {
        params: {
          query: {
            date: params.date,
            ...(params.userId ? { userId: params.userId } : {}),
            ...(params.cursor ? { cursor: params.cursor } : {}),
            limit: 200,
          },
          header: purposeHeader(purpose),
        },
      });
      return required(data);
    },
    enabled: params !== null && purpose !== null,
    meta: { silent: true },
  });
}

// ---- Auditoria (REQ-AUD-005/006; kernel) ----

export type AuditScope =
  | { readonly kind: 'network' }
  | { readonly kind: 'tenant'; readonly tenantId: string }
  | { readonly kind: 'study'; readonly tenantId: string; readonly studySiteId: string };

export interface AuditFilters {
  readonly entityType?: string | undefined;
  readonly entityId?: string | undefined;
  /** Histórico por pessoa (OPEN-094): os eventos de todas as entidades da pessoa, correlacionados pelo `PersonId` da trilha. */
  readonly personId?: string | undefined;
  readonly actorId?: string | undefined;
  readonly eventType?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly studySiteId?: string | undefined;
  /** Só na rota de rede. */
  readonly tenantId?: string | undefined;
  readonly cursor?: string | undefined;
  readonly limit?: number | undefined;
}

function auditQuery(filters: AuditFilters) {
  return {
    ...(filters.entityType ? { entityType: filters.entityType } : {}),
    ...(filters.entityId ? { entityId: filters.entityId } : {}),
    ...(filters.personId ? { personId: filters.personId } : {}),
    ...(filters.actorId ? { actorId: filters.actorId } : {}),
    ...(filters.eventType ? { eventType: filters.eventType } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.studySiteId ? { studySiteId: filters.studySiteId } : {}),
    ...(filters.cursor ? { cursor: filters.cursor } : {}),
    ...(filters.limit ? { limit: filters.limit } : {}),
  };
}

export function auditScopeKey(scope: AuditScope): readonly string[] {
  return scope.kind === 'network'
    ? [...networkKey, 'audit']
    : scope.kind === 'tenant'
      ? [...tenantKey(scope.tenantId), 'audit']
      : [...tenantKey(scope.tenantId), 'audit', scope.studySiteId];
}

export function useAuditTrail(
  scope: AuditScope,
  filters: AuditFilters,
  purpose: DeclaredPurpose | null,
) {
  const client = useApiClient();
  return useQuery({
    queryKey: [...auditScopeKey(scope), filters, purpose?.code ?? null],
    queryFn: async (): Promise<AuditPage> => {
      if (!purpose) {
        throw new Error('the audit trail needs a declared purpose');
      }
      const header = purposeHeader(purpose);
      if (scope.kind === 'network') {
        const { data } = await client.GET('/api/v1/network/audit', {
          params: {
            query: {
              ...auditQuery(filters),
              ...(filters.tenantId ? { tenantId: filters.tenantId } : {}),
            },
            header,
          },
        });
        return required(data);
      }
      if (scope.kind === 'tenant') {
        const { data } = await client.GET('/api/v1/tenants/{tenantId}/audit', {
          params: { path: { tenantId: scope.tenantId }, query: auditQuery(filters), header },
        });
        return required(data);
      }
      const { data } = await client.GET('/api/v1/tenants/{tenantId}/studies/{studySiteId}/audit', {
        params: {
          path: { tenantId: scope.tenantId, studySiteId: scope.studySiteId },
          query: auditQuery(filters),
          header,
        },
      });
      return required(data);
    },
    enabled: purpose !== null,
    meta: { silent: true },
  });
}

export interface AuditExportArgs {
  readonly format: AuditExportFormat;
  readonly filters: AuditFilters;
  readonly purpose: DeclaredPurpose;
}

/** Exportação síncrona (`GET …/audit/export`): devolve o arquivo (JSONL/CSV com hashes) e o nome sugerido pelo servidor. */
export function useAuditExport(scope: AuditScope) {
  const client = useApiClient();
  return useMutation({
    mutationFn: async (args: AuditExportArgs): Promise<{ blob: Blob; fileName: string }> => {
      const header = purposeHeader(args.purpose);
      const query = { ...auditQuery(args.filters), format: args.format };
      const result =
        scope.kind === 'network'
          ? await client.GET('/api/v1/network/audit/export', {
              params: {
                query: {
                  ...query,
                  ...(args.filters.tenantId ? { tenantId: args.filters.tenantId } : {}),
                },
                header,
              },
              parseAs: 'blob',
            })
          : scope.kind === 'tenant'
            ? await client.GET('/api/v1/tenants/{tenantId}/audit/export', {
                params: { path: { tenantId: scope.tenantId }, query, header },
                parseAs: 'blob',
              })
            : await client.GET('/api/v1/tenants/{tenantId}/studies/{studySiteId}/audit/export', {
                params: {
                  path: { tenantId: scope.tenantId, studySiteId: scope.studySiteId },
                  query,
                  header,
                },
                parseAs: 'blob',
              });
      const blob = result.data;
      if (!blob) {
        throw new Error('Empty export');
      }
      const disposition = result.response.headers.get('Content-Disposition') ?? '';
      const match = /filename="?([^";]+)"?/.exec(disposition);
      return { blob, fileName: match?.[1] ?? `audit.${args.format}` };
    },
    meta: { silent: true },
  });
}

export interface AuditChainStatusResponse {
  readonly isValid: boolean;
  readonly eventsChecked: number;
  readonly firstBrokenSequence: number | null;
}

export function useVerifyAuditChain() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (purpose: DeclaredPurpose): Promise<AuditChainStatusResponse> => {
      const header = purposeHeader(purpose);
      const { response } = await client.GET('/api/v1/network/audit/chain-status', {
        params: { header },
        parseAs: 'stream',
      });
      const raw = (await response.json()) as Partial<AuditChainStatusResponse>;
      return {
        isValid: raw.isValid ?? true,
        eventsChecked: raw.eventsChecked ?? 0,
        firstBrokenSequence: raw.firstBrokenSequence ?? null,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [...networkKey, 'audit'] });
    },
    meta: { silent: true },
  });
}

/** Entrega o arquivo ao navegador (URL de objeto; revogada logo a seguir). */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
